import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../../layout";
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Divider,
  TableRow,
  TableCell,
  Chip,
  Box,
  CircularProgress,
  IconButton,
} from "@mui/material";
import ContainedButton from "../../forms/button/ContainedButton";
import CustomDataTable from "../../components/table/CustomTable";
import {
  getTariffsV2,
  TariffV2,
  getTariffDetailsV2,
} from "../../utils/tariffServiceV2";
import { formatDate } from "../../utils/helper";
import CustomToast from "../../components/toast/CustomToast";
import { ToastState } from "../../types";

import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TariffDetailsModal from "./TariffDetailsModal";

const COLUMNS = [
  "Tariff Code",
  "Name",
  "Description",
  "Effective From",
  "Effective To",
  "Status",
  "Actions",
];

const NewTariffs: React.FC = () => {
  const [tariffs, setTariffs] = useState<TariffV2[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTariff, setSelectedTariff] = useState<TariffV2 | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [toast, setToast] = useState<ToastState>({
    message: "",
    severity: "",
  });
  const navigate = useNavigate();

  const fetchTariffs = async () => {
    try {
      setLoading(true);
      const response = await getTariffsV2();
      if (response && response.success) {
        setTariffs(response.data);
      } else {
        setToast({
          message: response?.errors || "Failed to fetch tariffs",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error fetching tariffs:", error);
      setToast({
        message: "An error occurred while fetching tariffs",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTariffs();
  }, []);

  const [searchParams] = useSearchParams();
  const tariffId = searchParams.get("id");

  useEffect(() => {
    if (tariffId) {
      const fetchSingleTariff = async () => {
        try {
          const res = await getTariffDetailsV2(tariffId);
          if (res && res.success) {
            setSelectedTariff(res.data);
            setIsDetailsModalOpen(true);
          }
        } catch (err) {
          console.error("Error fetching tariff details via query param", err);
        }
      };
      fetchSingleTariff();
    }
  }, [tariffId]);

  const handleOpenDetails = (tariff: TariffV2) => {
    setSelectedTariff(tariff);
    setIsDetailsModalOpen(true);
  };

  const rowsContent = tariffs.map((item, index) => (
    <TableRow
      key={item.id || index}
      sx={{ cursor: "pointer", "&:hover": { backgroundColor: "#f5f5f5" } }}
      onClick={() => handleOpenDetails(item)}
    >
      <TableCell>
        <Typography variant="body2">{item.tariffCode}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight={600}>
          {item.name}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{item.description}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          {formatDate(item.effectiveFrom, "DD-MM-YYYY HH:mm")}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          {formatDate(item.effectiveTo, "DD-MM-YYYY HH:mm")}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={item.isActive ? "Active" : "Inactive"}
          color={item.isActive ? "success" : "default"}
          size="small"
          sx={{ fontWeight: 500 }}
        />
      </TableCell>
      <TableCell>
        <Stack direction="row" gap={1}>
          <IconButton
            size="small"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              handleOpenDetails(item);
            }}
            sx={{ color: "primary.main" }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              navigate(`/new-tariffs/form`, { state: { tariff: item } });
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Stack>
      </TableCell>
    </TableRow>
  ));

  return (
    <Layout>
      <Card sx={{ borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          p={2}
        >
          <Typography variant="h5" fontWeight={700}>
            New Tariffs (v2)
          </Typography>
          <Stack direction="row" gap={2}>
            <ContainedButton
              label="Add Tariff"
              onClick={() => navigate("/new-tariffs/form")}
              sx={{ px: 3 }}
            />
            <ContainedButton
              label="Refresh"
              onClick={fetchTariffs}
              loading={loading}
              sx={{
                px: 3,
                backgroundColor: "#1976d2",
                color: "#ffffff",
                "&:hover": { backgroundColor: "#1565c0" },
              }}
            />
          </Stack>
        </Stack>
        <Divider />
        <CardContent>
          {loading && tariffs.length === 0 ? (
            <Box display="flex" justifyContent="center" p={5}>
              <CircularProgress />
            </Box>
          ) : (
            <CustomDataTable
              rowsContent={rowsContent}
              columns={COLUMNS}
              isLoading={loading}
            />
          )}
        </CardContent>
      </Card>

      <TariffDetailsModal
        open={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        tariff={selectedTariff}
      />

      <CustomToast
        open={toast.message}
        severity={toast.severity as any}
        setToast={setToast}
      />
    </Layout>
  );
};

export default NewTariffs;
